/**
 * Settings Page
 *
 * Main settings page with tabbed navigation and deep linking support.
 * Each tab's content is displayed within the same page structure.
 */

import { useEffect, useMemo } from 'react';
import { useSearchParams, Outlet } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { Tabs, type Tab } from '@/components/ui';
import { useBrandContext } from '@/hooks';
import { usePermission } from '@/hooks/usePermission';
import { BrandInfoTab } from '../components/tabs/BrandInfoTab';
import { CategoriesTab } from '../components/tabs/CategoriesTab';
import { SuppliersTab } from '../components/tabs/SuppliersTab';
import { PackagingsTab } from '../components/tabs/PackagingsTab';
import { MenusTab } from '../components/tabs/MenusTab';
import { UsersTab } from '../components/tabs/UsersTab';
import { NotificationsTab } from '../components/tabs/NotificationsTab';
import { InventoryListsSettingsTab } from '../../inventory-lists';

type SettingsTabId = 'informations' | 'categories' | 'units' | 'suppliers' | 'packagings' | 'menus' | 'inventory-lists' | 'users' | 'notifications';

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isGroup } = useBrandContext();

  // Permissions pour chaque onglet
  const canReadUsers = usePermission('users.read');
  const canReadSuppliers = usePermission('suppliers.read');

  // Filter tabs based on brand context and permissions
  const settingsTabs = useMemo(() => {
    const tabs: Tab[] = [];

    // Informations - visible pour MANAGER+
    if (canReadSuppliers) {
      tabs.push({ id: 'informations', label: 'Informations' });
    }

    // Categories - groupOnly, MANAGER+ (via suppliers.read comme proxy)
    if (isGroup && canReadSuppliers) {
      tabs.push({ id: 'categories', label: 'Categories' });
    }

    // Fournisseurs - groupOnly, MANAGER+
    if (isGroup && canReadSuppliers) {
      tabs.push({ id: 'suppliers', label: 'Fournisseurs' });
    }

    // Conditionnements - groupOnly, MANAGER+
    if (isGroup && canReadSuppliers) {
      tabs.push({ id: 'packagings', label: 'Conditionnements' });
    }

    // Menus POS - groupOnly, MANAGER+
    if (isGroup && canReadSuppliers) {
      tabs.push({ id: 'menus', label: 'Menus POS' });
    }

    // Listes d'inventaire - groupOnly (parent brand manages lists)
    if (isGroup && canReadSuppliers) {
      tabs.push({ id: 'inventory-lists', label: "Listes d'inventaire" });
    }

    // Utilisateurs - ADMIN+ uniquement
    if (canReadUsers) {
      tabs.push({ id: 'users', label: 'Utilisateurs' });
    }

    // Notifications - visible pour tous (préférences personnelles)
    tabs.push({ id: 'notifications', label: 'Notifications' });

    return tabs;
  }, [isGroup, canReadUsers, canReadSuppliers]);

  // Default to first available tab
  const defaultTab = settingsTabs[0]?.id as SettingsTabId || 'units';

  // Get tab from URL or use default
  const tabFromUrl = searchParams.get('tab') as SettingsTabId | null;
  const activeTab = settingsTabs.some(t => t.id === tabFromUrl)
    ? tabFromUrl!
    : defaultTab;

  // Sync URL when tab changes
  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  // Set default tab in URL if not present
  useEffect(() => {
    if (!tabFromUrl || !settingsTabs.some(t => t.id === tabFromUrl)) {
      setSearchParams({ tab: defaultTab }, { replace: true });
    }
  }, [tabFromUrl, setSearchParams, settingsTabs, defaultTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'informations':
        return <BrandInfoTab />;
      case 'categories':
        return <CategoriesTab />;
      case 'suppliers':
        return <SuppliersTab />;
      case 'packagings':
        return <PackagingsTab />;
      case 'menus':
        return <MenusTab />;
      case 'inventory-lists':
        return <InventoryListsSettingsTab />;
      case 'users':
        return <UsersTab />;
      case 'notifications':
        return <NotificationsTab />;
      default:
        return <CategoriesTab />;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Settings className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-dark-500">
              Parametres
            </h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Gerez les referentiels et configurations de votre ERP
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-neutral-200 px-6 flex-shrink-0">
        <Tabs
          tabs={settingsTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {renderTabContent()}
      </div>

      {/* Outlet for deep linking (suppliers catalog, price history) */}
      <Outlet />
    </div>
  );
}
